/** Labels and styling for App Builder chat messages (Emergent-style clarity). */

const MODE_META = {
  change_request: { label: 'You', tone: 'user' },
  plan_request: { label: 'You', tone: 'user' },
  auto_apply: { label: 'Applying changes', tone: 'agent' },
  apply_complete: { label: 'Changes applied', tone: 'success' },
  plan_ready: { label: 'Plan ready', tone: 'plan' },
  plan_drafting: { label: 'Planning', tone: 'agent' },
  clarification: { label: 'Quick question', tone: 'question' },
  assumptions: { label: 'Assumptions', tone: 'muted' },
  requirements: { label: 'Requirements', tone: 'question' },
  needs_human_review: { label: 'Needs review', tone: 'warning' },
  'openlovable-llm-only': { label: 'Preview ready', tone: 'success' },
  verification: { label: 'Quality check', tone: 'muted' },
  orchestrator: { label: 'Pipeline', tone: 'activity' },
  initial_build: { label: 'New build', tone: 'agent' },
  full_rebuild: { label: 'Full rebuild', tone: 'agent' },
};

const KIND_META = {
  status: { label: 'Status', tone: 'activity' },
  backend: { label: 'Backend', tone: 'activity' },
  orchestrator: { label: 'Pipeline', tone: 'activity' },
  warning: { label: 'Warning', tone: 'warning' },
  error: { label: 'Error', tone: 'warning' },
  verify: { label: 'Verification', tone: 'activity' },
  qa: { label: 'Quality check', tone: 'activity' },
  design: { label: 'Design review', tone: 'activity' },
  phase: { label: 'Agent', tone: 'agent' },
  thinking: { label: 'Thinking', tone: 'thinking' },
  command: { label: 'Terminal', tone: 'command' },
  browser: { label: 'Browser QA', tone: 'browser' },
  tool: { label: 'Tool', tone: 'command' },
  mcp: { label: 'MCP', tone: 'command' },
  api: { label: 'API', tone: 'command' },
  edit: { label: 'Auto Fix', tone: 'edit' },
};

const TONE_CLASSES = {
  user: 'text-violet-300',
  agent: 'text-sky-300',
  success: 'text-emerald-300',
  plan: 'text-amber-300',
  question: 'text-amber-200',
  warning: 'text-orange-300',
  muted: 'text-zinc-400',
  activity: 'text-emerald-300/90',
  thinking: 'text-violet-300',
  command: 'text-lime-300',
  browser: 'text-cyan-300',
  edit: 'text-sky-300',
};

export function formatChatTimestamp(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const day = d.toLocaleDateString([], { day: 'numeric', month: 'long' });
  return `${time} ${day}`;
}

export function parseThinkingContent(content) {
  const raw = (content || '').trim();
  if (!raw) return { header: 'Thinking', body: '', progressPct: null, fixing: '', nextPlan: '' };
  const progressMatch = raw.match(/Progress:\s*~(\d+)%/i);
  const progressPct = progressMatch ? Number(progressMatch[1]) : null;
  let withoutProgress = raw.replace(/\n?\n?Progress:\s*~?\d+%\s*$/i, '').trim();
  const fixingMatch = withoutProgress.match(/\nFixing\n([\s\S]*?)(?=\nNext plan|\nNext:|\nProgress:|$)/i);
  const nextMatch = withoutProgress.match(/\nNext plan\n([\s\S]*?)(?=\nFixing|\nProgress:|$)/i)
    || withoutProgress.match(/\nNext:\s*([\s\S]*?)(?=\nFixing|\nProgress:|$)/i);
  const fixing = fixingMatch ? fixingMatch[1].trim() : '';
  const nextPlan = nextMatch ? nextMatch[1].trim() : '';
  withoutProgress = withoutProgress
    .replace(/\nFixing\n[\s\S]*?(?=\nNext plan|\nNext:|\nProgress:|$)/i, '')
    .replace(/\nNext plan\n[\s\S]*?(?=\nFixing|\nProgress:|$)/i, '')
    .replace(/\nNext:\s*[\s\S]*?(?=\nFixing|\nProgress:|$)/i, '')
    .trim();
  const lines = withoutProgress.split('\n');
  const header = (lines[0] || 'Thinking').trim();
  const body = lines.slice(1).join('\n').trim() || withoutProgress;
  return { header, body, progressPct, fixing, nextPlan };
}

export function chatMessageMeta(message) {
  if (!message) return { label: 'Agent', tone: 'agent', toneClass: TONE_CLASSES.agent };
  if (message.role === 'user') {
    return { label: message.label || 'You', tone: 'user', toneClass: TONE_CLASSES.user };
  }
  if (message.role === 'activity' || message.role === 'edit') {
    const kind = message.kind || (message.role === 'edit' ? 'edit' : 'status');
    const meta = KIND_META[kind] || { label: message.label || 'Status', tone: 'activity' };
    const label = message.label || message.agent || meta.label;
    return { ...meta, label, toneClass: TONE_CLASSES[meta.tone] || TONE_CLASSES.activity };
  }
  const mode = message.mode;
  const meta = (mode && MODE_META[mode]) || { label: message.label || 'Agent', tone: 'agent' };
  return { ...meta, toneClass: TONE_CLASSES[meta.tone] || TONE_CLASSES.agent };
}

export function activityLabel(kind) {
  return (KIND_META[kind] || { label: 'Status' }).label;
}

export function pipelineProgressPct(phases = []) {
  if (!Array.isArray(phases) || !phases.length) return 0;
  const terminal = new Set(['done', 'skipped', 'error']);
  const completed = phases.filter((p) => terminal.has(p?.status)).length;
  const hasRunning = phases.some((p) => p?.status === 'running');
  let pct = Math.round((completed / phases.length) * 100);
  if (hasRunning && pct < 99) pct = Math.max(pct, 5);
  return Math.min(100, pct);
}
